import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, ArrowLeft, Info, Lock } from "lucide-react";
import { AI_ACTIONS, formatTimestamp, todayInTz, weekRange, type AiAction } from "@brainlog/shared";
import { get } from "@/lib/api";
import { useMe, useNote, useWorkspaces } from "@/lib/queries";
import { openView, useCurrentWorkspaceId, type Tab } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getEditorBridge } from "@/features/notes/editorBridge";
import { NotePicker, useLibrary } from "@/features/records/pickers";
import type { Availability } from "./types";
import { RunFlow } from "./RunFlow";
import { MakeSlidesFlow } from "@/features/slides/MakeSlides";
import { QuestionnaireFlow } from "@/features/learning/StartSession";
import { useCompare } from "@/features/library/compare";
import { cn } from "@/lib/utils";

interface Ctx {
  workspaceId: string;
  workspaceKind: string;
  noteId?: string;
  noteKind?: string;
  noteTitle?: string;
  paperId?: string;
  manuscriptId?: string;
  view?: string;
}

const DESCRIPTIONS: Record<AiAction, string> = {
  summarize: "A short summary of this note (or your selection) to insert or save.",
  extract_tasks: "Actionable items with their source passages, for you to edit and add to the backlog.",
  explore_topic: "Angles, questions and an outline for a topic — suggestions, not facts.",
  recent_updates: "Recent items from a web search, with source links and dates.",
  reading_note: "A reading note drafted from the PDF's extracted pages, with page references.",
  compare_papers: "Selected papers side by side, where they agree and differ, and what it means for your project; missing facts stay unknown.",
  ask_project: "An answer from this project's notes, records and paper pages, with links to sources.",
  next_steps: "Proposed next tasks with reasons, based on the brief, evidence, decisions and backlog.",
  progress_summary: "A summary of events recorded in a date range, with links to the records.",
  build_outline: "Proposed section headings with questions and linked sources.",
  draft_section: "Draft text for this section from your instructions and selected evidence.",
  review_draft: "Issues in structure, argument, unsupported claims and missing information.",
  check_requirements: "Your pasted guidelines compared with recorded manuscript information.",
  plan_revisions: "Proposed changes and tasks for each reviewer request.",
  response_to_reviewers: "Draft responses based only on confirmed changes.",
  deep_read: "Reads the whole paper and writes a detailed reading note in plain English: claims with page numbers, limitations, future work, references to check and what it means for your project. Saved in Literature and added to the literature matrix.",
  section_brief: "Round 1 of writing a manuscript section with a chosen method: a brief, questions and a plan. Run it from the section's “Write with AI” button.",
  section_write: "Round 2: writes the manuscript section from the agreed brief with the chosen method, citing only papers in the library.",
  section_edit: "Edits the selected text or a manuscript section with a chosen method (prose, AI style), keeping claims and citations.",
  manuscript_review: "An AI review round of the whole manuscript: reviewers score this version afresh and comment on exact passages.",
  revision_fix: "Proposes the exact text changes that address one review comment.",
  project_setup: "From your project proposal: the overview, research questions or hypotheses, milestones and suggested reading, for you to review.",
  mail_triage: "Tasks, dates and whether a reply is needed, from an email conversation. Run it from Correspondence.",
  mail_reply: "A reply to an email conversation, saved as a draft you copy or open in your mail program. Run it from Correspondence.",
  learn_step: "The next lesson of a learning session. Start one with Learn on a paper, a note or the project overview.",
  grill_step: "The next round of questions of a plan review. Start one with Grill the plan on a note, a manuscript or the project overview.",
  glossary: "The project's terms, what they mean here and the words to avoid, for you to choose; kept in the Glossary note, which the Terminology check uses.",
  questionnaire: "A questionnaire for a co-author or collaborator who knows what you need, saved as a note.",
  make_slides: "Slides for a talk from this note, paper, manuscript or project: titles, short points and speaker notes, saved as a Slides note in the Slides folder.",
};

export function AssistantPanel({ tab }: { tab: Tab | null }) {
  const currentWs = useCurrentWorkspaceId();
  const note = useNote(tab?.kind === "note" ? tab.id : undefined);
  const workspaces = useWorkspaces();
  const paper = useQuery({ queryKey: ["library-item", tab?.id], queryFn: () => get<any>(`/api/library/${tab!.id}`), enabled: tab?.kind === "paper" && !!tab.id });
  const ctx: Ctx | null = useMemo(() => {
    if (tab?.kind === "note" && note.data) {
      return {
        workspaceId: note.data.note.workspaceId,
        workspaceKind: note.data.workspace.kind,
        noteId: note.data.note.id,
        noteKind: note.data.note.kind,
        noteTitle: note.data.note.title,
        manuscriptId: note.data.manuscriptSections[0]?.manuscriptId,
      };
    }
    const wsId = (tab?.kind === "paper" ? paper.data?.item.workspaceId : tab?.workspaceId) ?? currentWs;
    const ws = workspaces.data?.find((w) => w.id === wsId);
    if (!ws) return null;
    return {
      workspaceId: ws.id,
      workspaceKind: ws.kind,
      paperId: tab?.kind === "paper" ? paper.data?.item.id : undefined,
      manuscriptId: tab?.view === "manuscript" ? tab.id : undefined,
      view: tab?.view,
    };
  }, [tab, note.data, paper.data, currentWs, workspaces.data]);

  const avail = useQuery({
    queryKey: ["ai-availability", ctx?.workspaceId],
    queryFn: () => get<Availability>(`/api/ai/availability?workspaceId=${ctx!.workspaceId}`),
    enabled: !!ctx,
  });
  const [action, setActionState] = useState<AiAction | null>(null);
  // Papers handed over by the library's "Compare with AI" (null: chosen here).
  const [preset, setPreset] = useState<{ ids: string[]; nonce: number } | null>(null);
  const setAction = (a: AiAction | null) => {
    setActionState(a);
    setPreset(null);
  };
  useEffect(() => setAction(null), [tab?.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const handoff = useCompare((s) => s.assistant);
  useEffect(() => {
    if (!handoff || !ctx || handoff.workspaceId !== ctx.workspaceId) return;
    setActionState("compare_papers");
    setPreset({ ids: handoff.ids, nonce: handoff.nonce });
    useCompare.setState({ assistant: null });
  }, [handoff?.nonce, ctx?.workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ctx) return <p className="p-3 text-sm text-muted-foreground">Open a notebook, project, note or paper to use the assistant.</p>;
  // End-to-end encrypted content never goes to the assistant — not even a selected passage.
  const encryptedWs = workspaces.data?.find((w) => w.id === ctx.workspaceId)?.encryptionScopeId;
  const kindName = ctx.workspaceKind === "notebook" ? "notebook" : "project";
  if (encryptedWs || (tab?.kind === "note" && note.data?.note.encryptionScopeId))
    return (
      <p className="flex items-start gap-2 p-3 text-sm text-muted-foreground">
        <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />
        {encryptedWs
          ? `This ${kindName} is end-to-end encrypted. The assistant is turned off for it, and nothing from it is ever sent to an AI.`
          : "This note is end-to-end encrypted. The assistant cannot read it, and nothing from it is ever sent to an AI."}
      </p>
    );
  const a = avail.data;

  const actions: AiAction[] = [];
  if (ctx.noteId) {
    actions.push("summarize", "extract_tasks", "explore_topic", "review_draft");
    if (ctx.noteKind === "manuscript_section") actions.push("draft_section");
  }
  if (ctx.paperId) actions.push("deep_read", "reading_note");
  if (ctx.manuscriptId) actions.push("build_outline", "check_requirements", "plan_revisions", "response_to_reviewers");
  if (ctx.view === "library" || ctx.view === "matrix" || ctx.paperId) actions.push("compare_papers");
  if (ctx.workspaceKind === "project") actions.push("ask_project", "next_steps", "progress_summary");
  if (ctx.noteId || ctx.paperId || ctx.manuscriptId || ctx.workspaceKind === "project") actions.push("make_slides");
  if (ctx.workspaceKind === "project") actions.push("glossary");
  actions.push("questionnaire");
  actions.push("recent_updates");
  if (!ctx.noteId && !actions.includes("explore_topic")) actions.push("explore_topic");
  const uniqueActions = [...new Set(actions)];

  return (
    <div className="space-y-3 p-3">
      {a && (
        <div className="space-y-1 rounded-md border bg-background p-2 text-xs">
          <p className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium">AI:</span> {a.provider.label}
            {a.provider.isDemo && (
              <Badge variant="outline" className="border-ai text-ai">
                Practice mode
              </Badge>
            )}
          </p>
          <p>
            <span className="font-medium">Where your text goes:</span>{" "}
            {a.provider.location === "external"
              ? `to ${a.provider.destination ?? "an AI service"}, outside BrainLog`
              : a.provider.location === "local"
                ? a.provider.isDemo
                  ? "nowhere. Practice mode runs inside BrainLog."
                  : "to an AI service run by your organisation"
                : "nowhere. No AI service is set up."}
          </p>
          {a.externalProcessingDisabled && <p className="font-medium">Outside services (AI and web search) are turned off here.</p>}
          <p className="text-muted-foreground">The AI only makes suggestions. Nothing changes until you review and accept them.</p>
        </div>
      )}
      {!action && (
        <>
          <p className="text-xs text-muted-foreground">
            Works on:{" "}
            {ctx.noteId ? `the note “${ctx.noteTitle}”` : ctx.paperId ? "this paper" : ctx.manuscriptId ? "this manuscript" : ctx.workspaceKind === "notebook" ? "this notebook" : "this project"}
          </p>
          <ul className="space-y-1.5">
            {uniqueActions.map((key) => {
              const av = a?.availability[key];
              return (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => setAction(key)}
                    className={cn("w-full rounded-md border bg-background px-3 py-2 text-left hover:bg-accent", av && !av.available && "opacity-75")}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <Sparkles className="size-3.5 text-ai" aria-hidden /> {AI_ACTIONS.labels[key]}
                    </span>
                    <span className="block text-xs text-muted-foreground">{DESCRIPTIONS[key]}</span>
                    {av && !av.available && <span className="mt-0.5 block text-xs">Not available: {av.reason}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          <ProposalHistory workspaceId={ctx.workspaceId} noteId={ctx.noteId} manuscriptId={ctx.manuscriptId} onOpen={() => {}} />
        </>
      )}
      {action && (
        <div className="space-y-3">
          <Button size="xs" variant="ghost" onClick={() => setAction(null)}>
            <ArrowLeft /> All actions
          </Button>
          <h3 className="text-sm font-semibold">{AI_ACTIONS.labels[action]}</h3>
          {a?.availability[action] && !a.availability[action].available ? (
            <div className="rounded-md border p-2 text-sm" role="status">
              <p className="flex gap-1.5">
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden /> {a.availability[action].reason}
              </p>
              <Button size="xs" variant="link" className="px-0" onClick={() => openView("settings", ctx.workspaceId, "Settings", { section: "ai" })}>
                Open the AI settings here
              </Button>
            </div>
          ) : (
            action === "glossary" ? (
              <RunFlow action="glossary" workspaceId={ctx.workspaceId} params={{}} onBack={() => setAction(null)} />
            ) : action === "questionnaire" ? (
              <QuestionnaireFlow workspaceId={ctx.workspaceId} noteId={ctx.noteId} />
            ) : action === "make_slides" ? (
              <MakeSlidesFlow
                workspaceId={ctx.workspaceId}
                scope={ctx.noteId ? "note" : ctx.paperId ? "paper" : ctx.manuscriptId ? "manuscript" : "project"}
                id={ctx.noteId ?? ctx.paperId ?? ctx.manuscriptId ?? undefined}
              />
            ) : (
              <ActionForm key={`${action}:${preset?.nonce ?? 0}`} action={action} ctx={ctx} initialItemIds={preset?.ids} />
            )
          )}
        </div>
      )}
    </div>
  );
}

function ActionForm({ action, ctx, initialItemIds }: { action: AiAction; ctx: Ctx; initialItemIds?: string[] }) {
  const me = useMe();
  const [params, setParams] = useState<any | null>(null);
  const [useSelection, setUseSelection] = useState(false);
  const [topic, setTopic] = useState(ctx.noteTitle ?? "");
  const [question, setQuestion] = useState("");
  const [instructions, setInstructions] = useState("");
  const [noteIds, setNoteIds] = useState<string[]>(ctx.noteId && action === "explore_topic" ? [ctx.noteId] : []);
  const [itemIds, setItemIds] = useState<string[]>(initialItemIds ?? (ctx.paperId ? [ctx.paperId] : []));
  const wk = weekRange(todayInTz(me.data?.timezone));
  const [from, setFrom] = useState(wk.from);
  const [to, setTo] = useState(wk.to);
  const [commentSetId, setCommentSetId] = useState("");
  const library = useLibrary(ctx.workspaceId);
  const ms = useQuery({ queryKey: ["manuscript", ctx.manuscriptId], queryFn: () => get<any>(`/api/manuscripts/${ctx.manuscriptId}`), enabled: !!ctx.manuscriptId });
  const selection = ctx.noteId ? getEditorBridge(ctx.noteId)?.selection() : null;

  if (params) return <RunFlow action={action} workspaceId={ctx.workspaceId} params={params} onBack={() => setParams(null)} />;

  const build = (): any => {
    switch (action) {
      case "summarize":
      case "extract_tasks":
        return { noteId: ctx.noteId, selection: useSelection && selection?.text ? selection : null };
      case "explore_topic":
        return { topic, noteIds };
      case "recent_updates":
        return { topic };
      case "reading_note":
      case "deep_read":
        return { itemId: ctx.paperId };
      case "project_setup":
        return { proposalNoteId: "" };
      case "compare_papers":
        return { itemIds };
      case "ask_project":
        return { question };
      case "next_steps":
        return {};
      case "progress_summary":
        return { from, to };
      case "build_outline":
        return { manuscriptId: ctx.manuscriptId, noteIds };
      case "draft_section":
        return { noteId: ctx.noteId, instructions, evidenceNoteIds: noteIds };
      case "review_draft":
        return { noteId: ctx.noteId };
      case "check_requirements":
      case "response_to_reviewers":
        return { manuscriptId: ctx.manuscriptId };
      case "plan_revisions":
        return { commentSetId };
    }
  };
  const valid =
    (action !== "explore_topic" && action !== "recent_updates") || topic.trim().length >= 2
      ? action === "ask_project"
        ? question.trim().length >= 3
        : action === "compare_papers"
          ? itemIds.length >= 2
          : action === "draft_section"
            ? instructions.trim().length >= 3
            : action === "plan_revisions"
              ? !!commentSetId
              : action === "progress_summary"
                ? from <= to
                : true
      : false;

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) setParams(build());
      }}
    >
      {(action === "summarize" || action === "extract_tasks") && (
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={useSelection} disabled={!selection?.text} onChange={(e) => setUseSelection(e.target.checked)} />
          <span>
            Use only the text selected in the editor
            {selection?.text ? ` (${selection.text.length} characters)` : " — select text in the editor first"}
          </span>
        </label>
      )}
      {(action === "explore_topic" || action === "recent_updates") && (
        <div className="space-y-1">
          <Label htmlFor="ai-topic">Topic</Label>
          <Input id="ai-topic" value={topic} onChange={(e) => setTopic(e.target.value)} maxLength={300} />
          {action === "recent_updates" && <p className="text-xs text-muted-foreground">Your topic is sent to a web search service.</p>}
        </div>
      )}
      {action === "ask_project" && (
        <div className="space-y-1">
          <Label htmlFor="ai-q">Question</Label>
          <Textarea id="ai-q" rows={3} value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={1000} />
          <p className="text-xs text-muted-foreground">Answers only from this project's notes, records and papers, by matching your words. It does not search the wider literature.</p>
        </div>
      )}
      {action === "draft_section" && (
        <div className="space-y-1">
          <Label htmlFor="ai-instr">Your instructions</Label>
          <Textarea id="ai-instr" rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Write a cautious paragraph describing the pilot result and its caveats." />
        </div>
      )}
      {(action === "explore_topic" || action === "build_outline" || action === "draft_section") && (
        <div className="space-y-1">
          <Label>{action === "explore_topic" ? "Notes to include" : "Evidence records to use"} ({noteIds.length} selected)</Label>
          <NotePicker
            workspaceId={ctx.workspaceId}
            multiple
            selected={noteIds}
            excludeIds={action === "draft_section" && ctx.noteId ? [ctx.noteId] : []}
            onPick={(id) => setNoteIds(noteIds.includes(id) ? noteIds.filter((x) => x !== id) : [...noteIds, id])}
          />
        </div>
      )}
      {action === "compare_papers" && (
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium">Papers to compare (at least two) — {itemIds.length} selected</legend>
          <ul className="max-h-56 space-y-0.5 overflow-y-auto rounded-md border p-1 text-sm">
            {library.data?.items.map((i) => (
              <li key={i.id}>
                <label className="flex items-start gap-2 rounded px-1 py-0.5 hover:bg-accent">
                  <input type="checkbox" className="mt-1" checked={itemIds.includes(i.id)} onChange={(e) => setItemIds(e.target.checked ? [...itemIds, i.id] : itemIds.filter((x) => x !== i.id))} />
                  <span>{i.title || "Untitled source"}</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      )}
      {action === "progress_summary" && (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor="ai-from">From</Label>
            <Input id="ai-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ai-to">To</Label>
            <Input id="ai-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
      )}
      {action === "plan_revisions" && (
        <div className="space-y-1">
          <Label htmlFor="ai-set">Reviewer comments</Label>
          <select id="ai-set" className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={commentSetId} onChange={(e) => setCommentSetId(e.target.value)}>
            <option value="">Choose…</option>
            {ms.data?.commentSets.map((s: any) => (
              <option key={s.id} value={s.id}>
                {s.reviewerLabel}
              </option>
            ))}
          </select>
          {ms.data && !ms.data.commentSets.length && <p className="text-xs text-muted-foreground">Record reviewer comments in the manuscript first.</p>}
        </div>
      )}
      {action === "reading_note" && !ctx.paperId && <p className="text-sm">Open a paper first.</p>}
      <Button type="submit" size="sm" disabled={!valid}>
        Next: see what will be sent
      </Button>
    </form>
  );
}

const STATUS_LABELS: Record<string, string> = { succeeded: "Ready", queued: "Waiting", running: "Running", failed: "Failed", cancelled: "Cancelled", blocked: "Not run" };

function ProposalHistory({ workspaceId, noteId, manuscriptId }: { workspaceId: string; noteId?: string; manuscriptId?: string; onOpen(): void }) {
  const me = useMe();
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["proposals", workspaceId, noteId, manuscriptId],
    queryFn: () =>
      get<{ proposals: { id: string; action: AiAction; status: string; isDemo: boolean; createdAt: string; error: string | null }[] }>(
        `/api/ai/proposals?workspaceId=${workspaceId}${noteId ? `&targetNoteId=${noteId}` : ""}${manuscriptId ? `&manuscriptId=${manuscriptId}` : ""}`,
      ),
  });
  if (open) return <RunFlow existingId={open} workspaceId={workspaceId} onBack={() => setOpen(null)} />;
  if (!q.data?.proposals.length) return null;
  return (
    <section>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Your recent AI results</h3>
      <ul className="space-y-1 text-sm">
        {q.data.proposals.slice(0, 8).map((p) => (
          <li key={p.id}>
            <button type="button" className="w-full rounded px-2 py-1 text-left hover:bg-accent" onClick={() => setOpen(p.id)}>
              {AI_ACTIONS.labels[p.action]} <span className="text-xs text-muted-foreground">· {STATUS_LABELS[p.status] ?? p.status}{p.isDemo ? " · Practice mode" : ""} · {formatTimestamp(p.createdAt, me.data?.timezone)}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
